/* eslint-disable react/display-name */
import { FORM_ERROR, setIn } from "final-form"
import createDecorator from "final-form-calculate"
import { navigate } from "gatsby"
import pick from "lodash/pick"
import { useMemo } from "react"

import {
  marketingClient,
  useMutation,
  useQueryClient,
} from "@trueskin-web/apis"
import { addressClient } from "@trueskin-web/apis"
import { useAuth } from "@trueskin-web/context"
import { getAddressSaveErrors, getErrorMessage } from "@trueskin-web/functions"
import * as localesService from "@trueskin-web/locales"
import { storageService } from "@trueskin-web/services"
import i18next, { Routes } from "@trueskin-web/translations"

const decorators = [
  createDecorator({
    field: "country",
    updates: {
      "shippingAddress.country": (value) => value,
      "billingAddress.country": (value) => value,
    },
  }),
]

const baseAddressFields = [
  "salutation",
  "firstName",
  "lastName",
  "additionalAddress",
  "street",
  "city",
  "postalCode",
  "country",
  "phoneNumber",
]

const getUserDetails = (user) => ({
  country: user.country,
  birthDate: user.birthDate,
  billingAddress: pick(user.billingAddresses[0], baseAddressFields),
  isShippingAddressSameAsBillingAddress:
    user.isShippingAddressSameAsBillingAddress,
  ...(user.addresses[0] && {
    shippingAddress: pick(user.addresses[0], baseAddressFields),
  }),
})

const sanitizeFormValues = (formValues) => {
  formValues.billingAddress.country = formValues.country

  if (formValues.isShippingAddressSameAsBillingAddress) {
    delete formValues.shippingAddress
  } else {
    formValues.shippingAddress.country = formValues.country
  }

  return formValues
}

const getSubmitErrors = (submitError) => {
  let errors = {}

  const setError = (key, value) => {
    errors = setIn(errors, key, value)
  }

  const errorMessage = getErrorMessage(submitError)

  const { fields = {} } = submitError.data || submitError?.metadata || {}

  const addressError =
    errorMessage === "AddressValidationError" ||
    errorMessage === "ADDRESS_INVALID"

  if (fields.email) {
    setError("email", i18next.t("Auth.form.error.email.format"))
  } else if (addressError && fields.birthDate) {
    setError("birthDate", i18next.t("Generic.errors.birthDateInvalid"))
  } else if (addressError && fields.billingAddress?.postalCode) {
    setError(
      "billingAddress.postalCode",
      i18next.t("Generic.errors.invalidPostalCode")
    )
  } else if (addressError && fields.shippingAddress?.postalCode) {
    setError(
      "shippingAddress.postalCode",
      i18next.t("Generic.errors.invalidPostalCode")
    )
  } else if (
    addressError &&
    fields.billingAddress?.country === "COUNTRY_MUST_MATCH"
  ) {
    setError(
      "billingAddress.country",
      i18next.t("Generic.errors.billingCountryError")
    )
    setError(
      "shippingAddress.country",
      i18next.t("Generic.errors.shippingCountryError")
    )
  } else {
    setError(FORM_ERROR, errorMessage)
  }

  return errors
}

const trackErrors = (errorType, values) => {
  marketingClient.sendEvent({
    name: marketingClient.EVENT_TYPES_ENUM.ADDRESS_SAVE_ERROR,
    data: {
      errorType,
      phone: values.billingAddress.phoneNumber,
      country: values.country,
      city: values.billingAddress.city,
    },
  })
}

const withPersonalDetails = (Component) => {
  const WrappedComponent = (props) => {
    const queryClient = useQueryClient()

    const { user } = useAuth()

    const { mutateAsync: saveAddresses } = useMutation(
      addressClient.saveAddresses,
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.refetchQueries("userIdentity"),
            queryClient.refetchQueries("currentUser"),
            queryClient.refetchQueries("currentOrderSummary"),
          ])

          storageService.removeItem({
            id: storageService.getStorageKeys().USER_DETAILS,
          })
        },
      }
    )

    const userDetails = useMemo(() => {
      const localUserDetails = storageService.getItem({
        id: storageService.getStorageKeys().USER_DETAILS,
      })

      if (localUserDetails) {
        return localUserDetails
      }

      return getUserDetails(user)
    }, [user])

    const handleSubmit = async (formValues) => {
      let sanitizedFormValues
      try {
        sanitizedFormValues = sanitizeFormValues(formValues)

        await saveAddresses(sanitizedFormValues)
        localesService.saveCountry(sanitizedFormValues.country)

        navigate(Routes.App.Start.Payment.url)
      } catch (error) {
        const submitErrors = getSubmitErrors(error)
        handleFormValidationErrors(submitErrors, sanitizedFormValues)
        return submitErrors
      }
    }

    const handleFormValidationErrors = (errors, values) => {
      const errorTypes = getAddressSaveErrors(errors)

      errorTypes.map((errorType) => trackErrors(errorType, values))
    }

    const handlePageLeave = (formValues) =>
      storageService.setItem({
        id: storageService.getStorageKeys().USER_DETAILS,
        data: formValues,
      })

    return (
      <Component
        userDetails={userDetails}
        decorators={decorators}
        isDummyAccount={user.isDummy}
        onSubmit={handleSubmit}
        onError={handleFormValidationErrors}
        onPageLeave={handlePageLeave}
        {...props}
      />
    )
  }

  WrappedComponent.displayName = `withPersonalDetails(${
    Component.displayName || Component.name || "Component"
  })`

  return WrappedComponent
}

export default withPersonalDetails
